import {
  AttachMoney,
  Calculate,
  Info as InfoIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { AccountBalance } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { getDollarValue, getUfValue } from 'lib/api/cmf';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import { updateSGRWarrantyRequest } from 'lib/api/sgr';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import InfoCard from 'lib/designSystem/InfoCard';
import PageHeader from 'lib/designSystem/PageHeader';
import riskPremiumCalculator, {
  Output,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney, showAlert } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type CalculationResult = Output;

interface WarrantyEditConfirmationProps {
  open: boolean;
  formData: any;
  rateControls: RiskPremiumRates;
  isApproved: boolean;
  originalWarranty: Warranty;
  onClose: () => void;
  refreshWarranty?: () => void;
  setIsEditing: (isEditing: boolean) => void;
}

const WarrantyEditConfirmation: React.FC<WarrantyEditConfirmationProps> = ({
  open,
  formData,
  rateControls,
  isApproved,
  originalWarranty,
  onClose,
  refreshWarranty,
  setIsEditing,
}) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = updateSGRWarrantyRequest(dispatch);
  const { data: dollar } = getDollarValue(dispatch);
  const { data: uf } = getUfValue(dispatch);
  const { calculate } = riskPremiumCalculator();
  const [runDecisionEngine, setRunDecisionEngine] = useState(false);
  const [originalCalculation, setOriginalCalculation] =
    useState<CalculationResult | null>(null);
  const [modifiedCalculation, setModifiedCalculation] =
    useState<CalculationResult | null>(null);
  const { data: creditLineData } = getCreditLineByProduct(
    dispatch,
    originalWarranty.businessIdentifier ?? undefined,
    'sgr',
  );
  const { quotedAmount, quotedCurrency, startDate, endDate, type } = formData;

  const daysDifference = Math.ceil(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
  const amount = Number(quotedAmount);

  const originalWarrantyAmount = originalWarranty.quotedAmount || 0;

  const handleConfirm = async () => {
    const conversionRates = { UF: uf?.value, USD: dollar?.value, CLP: 1 };
    const data = {
      ...formData,
      quotedAmount: Number(formData.quotedAmount),
      conversionRate:
        conversionRates[
          formData.quotedCurrency as keyof typeof conversionRates
        ],
      businessIdentifier: originalWarranty.businessIdentifier,
      operationCostFixed: rateControls.fixedRate,
      operationCostVariable: rateControls.variableRate,
      discountRateFixed: rateControls.fixedDiscount,
      discountRateVariable: rateControls.variableDiscount,
      description: formData.description,
      runDecisionEngine,
      deleteUnitCode: formData.principalRut && !formData.unitCode,
    };

    mutate(
      { warrantyRequestId: originalWarranty.id, data },
      {
        onSuccess: () => {
          onClose();
          setIsEditing(false);
          if (refreshWarranty) refreshWarranty();
          showAlert(dispatch, 'success', 'Garantía actualizada exitosamente');
        },
      },
    );
  };

  useEffect(() => {
    const performCalculations = () => {
      try {
        const original = calculate({
          quotedAmount: originalWarrantyAmount,
          startDate: originalWarranty.startDate,
          endDate: originalWarranty.endDate,
          quotedCurrency: originalWarranty.quotedCurrency,
          rates: originalRates,
        });

        const modified = calculate({
          quotedAmount: amount,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          quotedCurrency,
          rates: rateControls,
        });

        setOriginalCalculation(original);
        setModifiedCalculation(modified);
      } catch (error) {
        setOriginalCalculation(null);
        setModifiedCalculation(null);
      }
    };

    performCalculations();
  }, [
    amount,
    daysDifference,
    quotedCurrency,
    type,
    rateControls,
    creditLineData?.subProducts,
    originalWarranty,
  ]);

  const originalPremium = originalCalculation?.amount || 0;
  const modifiedPremium = modifiedCalculation?.amount || 0;
  const premiumDifference = modifiedPremium - originalPremium;

  const originalRates = {
    variableRate: originalWarranty.operationCostVariable,
    fixedRate: originalWarranty.operationCostFixed,
    variableDiscount: originalWarranty.discountRateVariable,
    fixedDiscount: originalWarranty.discountRateFixed,
  };

  const hasRateChanges =
    rateControls.variableRate !== originalRates.variableRate ||
    rateControls.fixedRate !== originalRates.fixedRate ||
    rateControls.variableDiscount !== originalRates.variableDiscount ||
    rateControls.fixedDiscount !== originalRates.fixedDiscount;

  const originalWarrantyPremium = originalCalculation?.amount || 0;
  const totalPremiumDifference = modifiedPremium - originalWarrantyPremium;
  const hasSignificantChanges = Math.abs(totalPremiumDifference) > 1000;
  const variableRateChange = `${originalRates.variableRate?.toFixed(2)}% → ${rateControls.variableRate?.toFixed(2)}%`;
  const fixedRateChange = `${originalRates.fixedRate?.toFixed(2)} UF → ${rateControls.fixedRate?.toFixed(2)} UF`;
  const variableDiscountChange = `${originalRates.variableDiscount?.toFixed(2)}% → ${rateControls.variableDiscount?.toFixed(2)}%`;
  const fixedDiscountChange = `${originalRates.fixedDiscount?.toFixed(2)}% → ${rateControls.fixedDiscount?.toFixed(2)}%`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <PageHeader.TitleOnly
          title="Confirmación de Edición de Garantía"
          iconType="calculate"
          subtitle={hasRateChanges ? 'Tasas Modificadas' : 'Tasas Estándar'}
        />
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Grid container>
            <Grid item xs={3}>
              <InfoCard
                icon={<AttachMoney />}
                label="Monto de Garantía"
                value={formatMoney(amount) + ' ' + quotedCurrency}
                color="blue"
              />
            </Grid>
            <Grid item xs={3}>
              <InfoCard
                icon={<Calculate />}
                label="Costo de emisión"
                value={formatMoney(modifiedPremium) + ' CLP'}
                color="green"
              />
            </Grid>
            {hasSignificantChanges && (
              <Grid item xs={3}>
                <InfoCard
                  icon={
                    totalPremiumDifference > 0 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )
                  }
                  label="Variación"
                  value={formatMoney(totalPremiumDifference) + ' CLP'}
                  color={totalPremiumDifference > 0 ? 'green' : 'red'}
                />
              </Grid>
            )}
            <Grid item xs={3}>
              <InfoCard
                icon={<AccountBalance />}
                label="Fogape"
                value={formData.fogape ? `Si / ${formData.fogape}%` : 'No / -'}
                color="orange"
              />
            </Grid>
            <Grid item xs={3}></Grid>
          </Grid>

          {hasRateChanges && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.8rem', mb: 1 }}
                >
                  Cambios en Tasas:
                </Typography>
              </Grid>
              {rateControls.variableRate !== originalRates.variableRate && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Tasa Variable"
                    value={variableRateChange}
                    color={
                      (rateControls.variableRate ?? 0) >
                      (originalRates.variableRate ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.fixedRate !== originalRates.fixedRate && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Tasa Fija"
                    value={fixedRateChange}
                    color={
                      (rateControls.fixedRate ?? 0) >
                      (originalRates.fixedRate ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.variableDiscount !==
                originalRates.variableDiscount && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Descuento Variable"
                    value={variableDiscountChange}
                    color={
                      (rateControls.variableDiscount ?? 0) >
                      (originalRates.variableDiscount ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}

              {rateControls.fixedDiscount !== originalRates.fixedDiscount && (
                <Grid item xs={6}>
                  <InfoCard
                    icon={<Calculate />}
                    label="Descuento Fijo"
                    value={fixedDiscountChange}
                    color={
                      (rateControls.fixedDiscount ?? 0) >
                      (originalRates.fixedDiscount ?? 0)
                        ? 'green'
                        : 'red'
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={runDecisionEngine}
                onChange={(e) => setRunDecisionEngine(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon sx={{ fontSize: '1rem', color: 'info.main' }} />
                <Typography variant="body2">
                  Ejecutar motor de decisiones después de la actualización
                </Typography>
              </Box>
            }
          />
        </Box>

        {/* Alerts */}
        {hasRateChanges && (
          <AlertBanner
            title="Tasas Modificadas"
            message={`Las tasas han sido modificadas respecto a la cotización original. El costo de emisión ha variado en ${formatMoney(Math.abs(premiumDifference))} CLP (${premiumDifference > 0 ? 'incremento' : 'reducción'}).`}
            severity="warning"
          />
        )}

        {isApproved && (
          <AlertBanner
            title="Garantía Aprobada"
            message="Al editar una garantía aprobada, el documento de pago generado por el costo de emisión será eliminado automáticamente. Se creará un nuevo documento de pago con los valores actualizados."
            severity="info"
          />
        )}

        {hasSignificantChanges && (
          <AlertBanner
            title="Cambios Significativos Detectados"
            message={`Se han detectado cambios significativos en el costo de emisión (${formatMoney(Math.abs(totalPremiumDifference))} CLP). Se recomienda ejecutar el motor de decisiones para re-evaluar la garantía.`}
            severity="warning"
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleConfirm}
          loading={isPending}
          disabled={isPending}
        >
          Confirmar y Actualizar Garantía
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default WarrantyEditConfirmation;
